import { Fragment, h, ComponentChildren } from "preact";
import { useRef } from "preact/hooks";

import { useDidMountEffect } from "../../hooks";
import classname from "../../../foundation/utils/classname";

import IconDotSpinner from "../../Icons/IconDotSpinner";

import styles from "./DialogFooter.module.css";

type HTMLButtonType = "submit" | "button" | "reset";

interface Props {
    positiveButtonText?: string;
    negativeButtonText?: string;
    fallbackButtonText?: string;
    positiveButtonType?: HTMLButtonType;
    negativeButtonType?: HTMLButtonType;
    fallbackButtonType?: HTMLButtonType;
    positiveButtonDisabled?: boolean;
    negativeButtonDisabled?: boolean;
    fallbackButtonDisabled?: boolean;
    onPositiveButtonClick?: () => void;
    onNegativeButtonClick?: () => void;
    onFallbackButtonClick?: () => void;
    hidePositiveButton?: boolean;
    hideNegativeButton?: boolean;
    footer?: ComponentChildren;
    loading?: boolean;
    loadingText?: string;
    cssClass?: string;
}

function DialogFooter({
    positiveButtonText = "DONE",
    negativeButtonText = "CANCEL",
    fallbackButtonText,
    positiveButtonType = "submit",
    negativeButtonType = "submit",
    fallbackButtonType = "submit",
    positiveButtonDisabled,
    negativeButtonDisabled,
    fallbackButtonDisabled,
    onPositiveButtonClick,
    onNegativeButtonClick,
    onFallbackButtonClick,
    hidePositiveButton = false,
    hideNegativeButton = true,
    footer,
    loading,
    loadingText,
    cssClass
}: Props) {
    const positiveButtonRef = useRef<HTMLButtonElement>(null);

    useDidMountEffect(() => {
        positiveButtonRef.current?.focus();
    });

    return (
        <form method="dialog" class={classname(styles.buttonsContainer, cssClass, { [styles.loading]: loading, [styles.hasLoadingText]: Boolean(loadingText) })}>
            {footer && <div class={styles.footer}>{footer}</div>}
            {!loading && fallbackButtonText && (
                <button
                    class={classname(styles.secondary, styles.fallback)}
                    onClick={onFallbackButtonClick}
                    type={fallbackButtonType}
                    disabled={fallbackButtonDisabled}>
                    {fallbackButtonText}
                </button>
            )}
            {!loading && !hideNegativeButton && (
                <button
                    class={styles.secondary}
                    onClick={onNegativeButtonClick}
                    type={negativeButtonType}
                    disabled={negativeButtonDisabled}>
                    {negativeButtonText}
                </button>
            )}
            {!hidePositiveButton && (
                <button
                    ref={positiveButtonRef}
                    className={styles.primary}
                    onClick={onPositiveButtonClick}
                    type={positiveButtonType}
                    disabled={loading || positiveButtonDisabled}>
                    {loading ? (
                        <>
                            <IconDotSpinner class={styles.spinner} width={24}/>
                            {loadingText}
                        </>
                    ) : positiveButtonText}
                </button>
            )}
        </form>
    );
}

export default DialogFooter;
